<template>
  <base-toggle
    v-bind="{ value, disabled, required }"
    v-model="modelValue"
    class="items-start align-top i-flex"
    :class="brandClasses.wrapper.size[size]"
    data-test-id="vf-checkbox"
    @change="$emit('change', $event)"
  >
    <span
      class="shrink-0 duration forced-colors:b forced-colors:b-[ButtonBorder] peer-focus-visible:outline-offset-4 peer-focus-visible:outline-auto"
      :class="[
        brandClasses.control.base,
        brandClasses.control.size?.[size],
        {
          [controlClasses.default]: !checked && !disabled && !invalid,
          [controlClasses.selected]: checked && !disabled,
          [controlClasses.error]: invalid,
          [controlClasses.disabled]: !checked && disabled,
          [controlClasses.disabledSelected]: checked && disabled,
        },
      ]"
      data-test-id="vf-checkbox-icon"
      :style="{ 'border-radius': radius?.[size] }"
    >
      <vf-icon :class="checked ? 'op-100' : 'op-0'" name="check" :size="iconSize?.[size] || size" />
    </span>
    <span
      v-if="$slots.default"
      class="duration"
      :class="[
        brandClasses.label.size[size],
        disabled ? labelClasses.disabled : labelClasses.default,
        labelClass,
      ]"
      data-test-id="vf-checkbox-label"
    >
      <slot />
      <span v-if="required" aria-hidden="true">*</span>
    </span>
  </base-toggle>
</template>

<script lang="ts" setup>
import type { CSSClass } from '#types/common'

const { size = 'md', value, variant } = defineProps<{
  /**
   * Sets the value of the checkbox
   */
  value?: string
  /**
   * Determines the size of the checkbox and the amount of spacing between checkbox and label
   */
  size?: 'sm' | 'md'
  /**
   * Sets the disabled state of the checkbox
   */
  disabled?: boolean
  /**
   * Sets the invalid state of the checkbox
   */
  invalid?: boolean
  /**
   * Classes to apply to the label
   */
  labelClass?: CSSClass
  /**
   * Defines if the checkbox input is required for accessibility purposes
   */
  required?: boolean
  /**
   * Defines variant
   */
  variant?: 'inverse'
}>()

defineEmits<{ change: [value: boolean] }>()

const modelValue = defineModel<boolean | string[]>()

const { brandClasses, iconSize, radius } = useAppConfig().components.vf.checkbox

const checked = computed(() =>
  isArray(modelValue.value) && value ? modelValue.value.includes(value) : modelValue.value
)

const controlClasses = computed(() => brandClasses.control.variant[variant || 'base'])
const labelClasses = computed(() => brandClasses.label.variant[variant || 'base'])
</script>
